<template>
  <!-- fragment root: .job-info-header and .job-step-container must stay direct
       children of the .action-view-right flex column for the log scroll layout -->
  <div class="job-info-header">
    <div v-if="run.runError" class="run-error-banner">
      <SvgIcon name="octicon-alert" class="mr-2" />
      {{ run.runError }}
    </div>
    <div class="job-info-header-title">
      {{ currentJob.title }}
    </div>
    <div>
      <div class="job-info-header-detail">
        {{ currentJob.detail }}
      </div>
      <hr class="action-hr" />
    </div>
  </div>
  <div class="job-step-container">
    <div
      class="job-step-section"
      v-for="(jobStep, i) in currentJob.steps"
      :key="i"
    >
      <div
        class="job-step-summary"
        :class="{ 'step-expandable': isExpandable(jobStep.status) }"
        @click.stop="toggleStepLogs(i)"
      >
        <SvgIcon
          name="octicon-chevron-down"
          class="mr-3 step-chevron"
          v-show="
            isExpandable(jobStep.status) && currentJobStepsStates[i].expanded
          "
        />
        <SvgIcon
          name="octicon-chevron-right"
          class="mr-3 step-chevron"
          v-show="
            isExpandable(jobStep.status) && !currentJobStepsStates[i].expanded
          "
        />

        <SvgIcon
          name="octicon-check-circle-fill"
          class="green mr-3"
          style="color: var(--copia-teal-02)"
          v-if="jobStep.status === 'success'"
        />
        <SvgIcon
          name="octicon-skip"
          class="ui text grey mr-3"
          style="color: var(--copia-grey-03)"
          v-else-if="jobStep.status === 'skipped'"
        />
        <SvgIcon
          name="octicon-clock"
          class="ui text yellow mr-3"
          style="color: var(--copia-orange-01)"
          v-else-if="jobStep.status === 'waiting'"
        />
        <SvgIcon
          name="octicon-blocked"
          class="ui text yellow mr-3"
          style="color: var(--copia-orange-01)"
          v-else-if="jobStep.status === 'blocked'"
        />
        <SvgIcon
          name="octicon-meter"
          class="ui text mr-3"
          style="color: var(--copia-orange-01)"
          class-name="job-status-rotate"
          v-else-if="jobStep.status === 'running'"
        />
        <SvgIcon
          name="octicon-x-circle-fill"
          class="red mr-3"
          style="color: var(--copia-red-01)"
          v-else
        />

        <span class="step-summary-msg">
          {{ jobStep.summary }}

          <i
            v-if="jobStep.status === 'running'"
            class="notched spinner loading icon"
          />
        </span>
        <span class="step-summary-dur">{{ jobStep.duration }}</span>
      </div>

      <!-- the log elements could be a lot, do not use v-if to destroy/reconstruct the DOM -->
      <div
        class="job-step-logs"
        ref="logs"
        v-show="currentJobStepsStates[i].expanded"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, toRefs, useTemplateRef, watch } from "vue";
import { SvgIcon } from "../svg.js";
import {
  actionsFetch,
  createLogLine,
  parseLineCommand,
} from "./ActionRunView.js";

// Props
const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  runIndex: {
    type: String,
    default: "",
  },
  jobIndex: {
    type: String,
    default: "",
  },
  actionsUrl: {
    type: String,
    default: "",
  },
});

const store = props.store;
const { currentRun: run } = toRefs(store.viewData);

const isExpandable = (status) =>
  ["success", "running", "failure", "cancelled"].includes(status);

// internal state
const loadingAbortController = ref(null);
const intervalID = ref(null);
const currentJobStepsStates = ref([]);

const currentJob = ref({
  title: "",
  detail: "",
  steps: [],
});

// Template refs
const logs = useTemplateRef("logs");

// get the active container element, either the `job-step-logs` or the `job-log-list` in the `job-log-group`
const getLogsContainer = (idx) => {
  const el = logs.value[idx];
  return el._stepLogsActiveContainer ?? el;
};

const beginLogGroup = (stepIndex, line, cmd) => {
  const el = getLogsContainer(stepIndex);
  const details = document.createElement("details");
  details.className = "job-log-group";
  details.open = true;

  const summary = document.createElement("summary");
  summary.className = "job-log-group-summary";
  summary.appendChild(createLogLine(line, cmd));
  details.appendChild(summary);

  const childContainer = document.createElement("div");
  childContainer.className = "job-log-list";
  details.appendChild(childContainer);
  el.appendChild(details);

  // redirect future log lines into the group's child container
  logs.value[stepIndex]._stepLogsActiveContainer = childContainer;
};

const endLogGroup = (stepIndex) => {
  // stop redirecting, future lines go back to the step-level container
  delete logs.value[stepIndex]._stepLogsActiveContainer;
};

const appendLogs = (stepIndex, logLines) => {
  for (const line of logLines) {
    const { cmd, prefix } = parseLineCommand(line.message);

    if (cmd === "hidden") continue;

    if (cmd === "group") {
      // strip the command prefix from the group title
      const groupLine = {
        ...line,
        message: line.message.substring(prefix.length),
      };
      beginLogGroup(stepIndex, groupLine, cmd);
      continue;
    }

    if (cmd === "endgroup") {
      endLogGroup(stepIndex);
      continue;
    }

    // For error/command lines, strip the prefix from the displayed message
    let logLine = line;
    if (prefix) {
      logLine = { ...line, message: line.message.substring(prefix.length) };
    }

    const el = getLogsContainer(stepIndex);
    el.append(createLogLine(logLine, cmd));
  }
};

const fetchJob = async (signal) => {
  const logCursors = currentJobStepsStates.value.map((it, idx) => {
    // cursor is used to indicate the last position of the logs
    // it's only used by backend, frontend just reads it and passes it back, it and can be any type.
    // for example: make cursor=null means the first time to fetch logs, cursor=eof means no more logs, etc
    return { step: idx, cursor: it.cursor, expanded: it.expanded };
  });
  const resp = await actionsFetch(
    `${props.actionsUrl}/runs/${props.runIndex}/jobs/${props.jobIndex}`,
    JSON.stringify({ logCursors }),
    signal,
  );
  return await resp.json();
};

const loadJob = async () => {
  if (loadingAbortController.value) return;
  const abortController = new AbortController();
  loadingAbortController.value = abortController;
  try {
    const response = await fetchJob(abortController.signal);

    // if the request was aborted (stale), ignore the response
    if (abortController.signal.aborted) return;

    // save the state to the shared store and local refs, then the UI will be updated
    store.viewData.currentRun = response.state.run;
    store.viewData.runArtifacts = response.artifacts || [];
    store.viewData.loaded = true;
    currentJob.value = response.state.currentJob;

    // sync the currentJobStepsStates to store the job step states
    for (let i = 0; i < currentJob.value.steps.length; i++) {
      if (!currentJobStepsStates.value[i]) {
        currentJobStepsStates.value[i] = {
          cursor: null,
          expanded: false,
          manuallyCollapsed: false,
        };
      }
    }
    // append logs to the UI
    for (const stepLogs of response.logs.stepsLog ?? []) {
      // save the cursor, it will be passed to backend next time
      currentJobStepsStates.value[stepLogs.step].cursor = stepLogs.cursor;
      appendLogs(stepLogs.step, stepLogs.lines);
    }

    if (run.value.done && intervalID.value) {
      clearInterval(intervalID.value);
      intervalID.value = null;
    }
  } catch (e) {
    if (e instanceof TypeError || abortController.signal.aborted) return;
    throw e;
  } finally {
    if (loadingAbortController.value === abortController) {
      loadingAbortController.value = null;
    }
  }
};

const loadJobForce = () => {
  loadingAbortController.value?.abort();
  loadingAbortController.value = null;
  loadJob();
};

// show/hide the step logs for a step
const toggleStepLogs = (idx) => {
  const step = currentJob.value.steps[idx];
  if (!isExpandable(step?.status)) return;

  const state = currentJobStepsStates.value[idx];
  state.expanded = !state.expanded;
  if (state.expanded) {
    state.manuallyCollapsed = false;
    loadJobForce(); // try to load the data immediately instead of waiting for next timer interval
  } else {
    // track that the user manually collapsed this step
    state.manuallyCollapsed = true;
  }
};

// Polling is cleared permanently once the run is done; if the run comes back
// to life (a job re-run reloads the shared store and flips done), resume it.
watch(
  () => run.value.done,
  (done) => {
    if (!done && !intervalID.value) {
      loadJobForce();
      intervalID.value = setInterval(loadJob, 1000);
    }
  },
);

// Lifecycle
onMounted(() => {
  // load job data and then auto-reload periodically
  loadJob();
  intervalID.value = setInterval(loadJob, 1000);
});

onUnmounted(() => {
  if (intervalID.value) {
    clearInterval(intervalID.value);
  }
  loadingAbortController.value?.abort();
});
</script>

<style scoped lang="less">
.action-hr {
  margin: 0 10px;
  border-color: var(--copia-grey-01);
}

.job-info-header {
  .job-info-header-title {
    font-size: 125%;
    padding: 10px;
    font-weight: bold;
  }

  .job-info-header-detail {
    padding: 0 10px 10px;
  }
}

.run-error-banner {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin: 0 10px 6px;
  background-color: var(--copia-red-02);
  color: var(--copia-red-01);
  border: 1px solid var(--copia-red-01);
  border-radius: 4px;
  font-size: 13px;
}

.job-step-container {
  max-height: 100%;
  overflow: auto;

  .job-step-summary {
    padding: 5px 10px;
    display: flex;
    align-items: center;

    .step-chevron {
      visibility: hidden;
    }

    .step-summary-msg {
      flex: 1;
    }
    .step-summary-dur {
      margin-left: 16px;
    }
  }
  .job-step-summary.step-expandable {
    cursor: pointer;
    .step-chevron {
      visibility: visible;
    }
  }
  .job-step-summary.step-expandable:hover {
    background-color: var(--color-black-light);
  }
}
</style>
